import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import './DoubleFlightBoarding.css'
import { FlightDetails } from '../interface/flight.details'
import { BUCKET_URL } from '../utils/env-utils'
import AnimatedCodeshare from './AnimatedCodeshare'
import { getRemarkKey, RemarkToCodeMapping, TripType } from '@industronics/fids-utils'
import RemarksRotator from './RemarksRotator'
import StationsRotator from './StationsRotator'
import mahbLogo from '../assets/MAHB_LOGO.jpg'
import type { Announcement } from '../interface/device.details'
import ScrollingText from './scrolling/ScrollingText'

interface DoubleFlightBoardingProps {
  gate: string
  flights: [FlightDetails, FlightDetails]
  enableNextFlight?: boolean
  announcement?: Announcement
}

interface FlightColumnProps {
  gate: string
  flight: FlightDetails
  enableNextFlight?: boolean
}

function getRemarkBadgeColor(remarkKey: RemarkToCodeMapping | undefined): string {
  switch (remarkKey) {
    case RemarkToCodeMapping.DELAYED:
    case RemarkToCodeMapping.CANCELLED:
    case RemarkToCodeMapping.GATE_CLOSED:
    case RemarkToCodeMapping.GATE_CHANGE:
      return 'd4-remark--red'
    case RemarkToCodeMapping.FINAL_CALL:
      return 'd4-remark--amber'
    case RemarkToCodeMapping.BOARDING:
    case RemarkToCodeMapping.GO_TO_GATE:
    case RemarkToCodeMapping.GATE_OPEN:
    case RemarkToCodeMapping.OPEN:
      return 'd4-remark--green'
    case RemarkToCodeMapping.DEPARTED:
    case RemarkToCodeMapping.LANDED:
      return 'd4-remark--cyan'
    default:
      return 'd4-remark--default'
  }
}

const FlightColumn: React.FC<FlightColumnProps> = ({ gate, flight, enableNextFlight }) => {
  const isNext = enableNextFlight === false ? false : !!flight.isNext

  const flightNumberPrefix = flight.flightNumber?.split(' ')[0] ?? 'DEFAULT'
  const isGateChangeForThisGate = flight.isGateChange && flight.oldLoungeCode === gate;
  const globalStartTime = useRef(performance.now()).current;
  const remarkKey = useMemo(() => {
    if (isGateChangeForThisGate) return RemarkToCodeMapping.GATE_CHANGE
    return flight?.remarkCode?.key
  }, [isGateChangeForThisGate, flight?.remarkCode?.key])

  const stations = useMemo<string[]>(() => {
    if (flight.tripType === TripType.Arrival) {
      return flight.stations.slice(0, -1).flatMap(
        (station: { translations: any[] }) => station.translations?.map(t => t.value) ?? []
      );
    } else if (flight.tripType === TripType.Departure) {
      return flight.stations.slice(1).flatMap(
        (station: { translations: any[] }) => station.translations?.map(t => t.value) ?? []
      );
    }
    return [];
  }, [flight.stations, flight.tripType]);

  const remarksList = useMemo<string[]>(() => {
    if (isGateChangeForThisGate) {
      return [getRemarkKey(RemarkToCodeMapping.GATE_CHANGE)]
    }
    return Array.isArray(flight?.remarkCode?.translations)
      ? flight.remarkCode.translations.length
        ? flight.remarkCode.translations.map((item: { value: any }) => item.value)
        : [flight.remarkCode.description]
      : []
  }, [isGateChangeForThisGate, flight?.remarkCode?.translations])

  const boardingTime = useMemo(() => {
    if (!flight?.loungeBoardingTime && !flight?.schLoungeBoardingTime) return ''
    return moment(flight.loungeBoardingTime || flight.schLoungeBoardingTime).format('HH:mm')
  }, [flight?.loungeBoardingTime, flight?.schLoungeBoardingTime])

  const handleLogoError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      e.currentTarget.src = `${BUCKET_URL}/1366x0200/${flightNumberPrefix}.jpg`
    },
    [flightNumberPrefix],
  )

  const renderDayLabel = (time: moment.MomentInput) => {
    const m = moment(time)
    if (!m.utcOffset(8).isSame(moment(), 'day')) {
      return (
        <span className="d4-day-label">
          {m.format('DD MMM')}
        </span>
      )
    }
    return null
  }

  const badgeColor = isGateChangeForThisGate ? 'd4-remark--red' : getRemarkBadgeColor(remarkKey)

  return (
    <div className="design4-column">
      {/* Zone 1: Brand bar */}
      <div className="design4-header">
        <img
          src={`${BUCKET_URL}/1366x0200/${flightNumberPrefix}.bmp`}
          onError={handleLogoError}
          alt="Airline logo"
          className="design4-logo"
        />
      </div>

      {/* Zone 2: Hero — flight number, codeshare, destination */}
      <div className="design4-info">
        <div className="d4-hero-group">
          <div className='row'>
            <div className='col-md-7'>
              <h1 className="design4-flight-number">{flight.flightNumber}</h1>
            </div>
            <div className='col-md-5'>
              <div className="d4-codeshare-wrap">
                {flight.codeShareFlight.length > 0 && (
                  <AnimatedCodeshare
                    className="d4-codeshare-logo"
                    mainClassName="d4-codeshare-main"
                    labelClassName="d4-codeshare-label"
                    codeShareFlights={flight.codeShareFlight}
                  />
                )}
              </div>
            </div>
          </div>

          <h2 className="design4-destination">
            <StationsRotator
              lastIndex={-1}
              globalStartTime={globalStartTime}
              items={stations}
              mainFlightNumber={flight.flightNumberSuffix}
            />
          </h2>
        </div>

        {/* Row 3: Scheduled + Estimated */}
        <div className="row" style={{ marginBottom: '0.4rem' }}>
          <div className="col-md-7">
            <span className="d4-grid-label">SCHEDULED:</span>
            <span className="d4-grid-value d4-text-green" style={{ marginLeft: '0.5rem' }}>
              {flight?.scheduledTravelTime
                ? moment(flight.scheduledTravelTime).format('HH:mm')
                : '—'}
              {flight?.scheduledTravelTime && renderDayLabel(flight.scheduledTravelTime)}
            </span>
          </div>
          <div className="col-md-5">
            <span className="d4-grid-label">ESTIMATED:</span>
            <span className="d4-grid-value d4-text-red" style={{ marginLeft: '0.5rem' }}>
              {flight?.ETA
                ? moment(flight.ETA).format('HH:mm')
                : '—'}
              {flight?.ETA && renderDayLabel(flight.ETA)}
            </span>
          </div>
        </div>

        {/* Row 4: Boarding time */}
        <div className="row g-0 align-items-center">
          <div className="col-md-6">
            <span className="d4-grid-label">BOARDING:</span>
            <span className="d4-grid-value d4-text-yellow" style={{ marginLeft: '0.5rem' }}>
              {boardingTime || '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Remark badge or Next Flight badge */}
      {isNext ? (
        <div className="d4-remark-badge d4-remark--next">NEXT FLIGHT</div>
      ) : remarksList.length > 0 ? (
        <div className={`d4-remark-badge ${badgeColor} ${isGateChangeForThisGate ? 'blink-hard' : ''}`}>
          <RemarksRotator
            cssClass='d4-remark-text'
            globalStartTime={globalStartTime}
            mainFlightNumber={flight.flightNumber}
            items={remarksList}
            itemHeight={Math.round(window.innerHeight * 0.08)}
          />
        </div>
      ) : null}
    </div>
  )
}

const DoubleFlightBoarding: React.FC<DoubleFlightBoardingProps> = ({ gate, flights, enableNextFlight, announcement }) => {
  const [currentTime, setCurrentTime] = useState(moment())
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(moment()), 1000)
    return () => clearInterval(interval)
  }, []);

  const hours = currentTime.utcOffset(8).format('HH')
  const minutes = currentTime.utcOffset(8).format('mm')
  const dateStr = currentTime.utcOffset(8).format('MMM DD, ddd')

  return (
    <div className="design4-container">
      {/* Header */}
      <div className="fids-header">
        <span className="fids-header-label">
          <span className="fids-header-label-dim">GATE</span>
          {' '}
          <span className="fids-header-label-bold">{gate}</span>
        </span>
        <div className="fids-header-datetime">
          <span className="fids-header-date">{dateStr}</span>
        </div>
      </div>

      <div className="d4-columns">
        <FlightColumn gate={gate} flight={flights[0]} enableNextFlight={enableNextFlight} />
        <FlightColumn gate={gate} flight={flights[1]} enableNextFlight={enableNextFlight} />
      </div>

      {/* Footer */}
      <div className="fids-footer">
        {announcement?.messageContent?.lines?.length ? (
          <div className="fids-footer-marquee">
            <ScrollingText
              sentences={announcement.messageContent.lines}
              speed={announcement.messageContent.speed || 50}
              separator=" &bull; "
              backgroundColor={announcement.messageContent.backgroundColor ?? 'transparent'}
              textColor={announcement.messageContent.textColor ?? 'white'}
              fontFamily={announcement.messageContent.fontFamily ?? 'Arial, sans-serif'}
              fontSize={announcement.messageContent.fontSize ?? 16}
              noScroll={announcement.messageContent.noScroll ?? false}
            />
          </div>
        ) : (
          <div className="fids-footer-spacer" />
        )}
        <div className="fids-footer-divider" />
        <div className="fids-footer-logo">
          <img src={mahbLogo} alt="MAHB Logo" className="fids-footer-logo-img" />
        </div>
      </div>
    </div>
  )
}

export default DoubleFlightBoarding